// src/components/ui/ConfirmDialog.jsx
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Close as CloseIcon,
  Warning as WarningIcon,
  DeleteForever as DeleteIcon,
} from '@mui/icons-material';

/**
 * A reusable confirmation dialog component
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {string} props.title - Dialog title
 * @param {string|React.ReactNode} props.content - Dialog content text or element
 * @param {string} props.confirmText - Text for the confirm button
 * @param {string} props.cancelText - Text for the cancel button
 * @param {string} props.confirmButtonColor - Color for the confirm button
 * @param {function} props.onConfirm - Function to call when confirmed
 * @param {function} props.onCancel - Function to call when cancelled
 * @param {boolean} props.isDelete - Whether this is a delete confirmation (shows different icon)
 * @param {boolean} props.isLoading - Whether the confirm action is loading
 */
const ConfirmDialog = ({
  open,
  title = 'Confirm Action',
  content = 'Are you sure you want to proceed?',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmButtonColor = 'primary',
  onConfirm,
  onCancel,
  isDelete = false,
  isLoading = false,
}) => {
  const theme = useTheme();
  
  const handleConfirm = () => {
    if (onConfirm) onConfirm();
  };
  
  const handleCancel = () => {
    if (onCancel) onCancel();
  };
  
  const IconComponent = isDelete ? DeleteIcon : WarningIcon;
  const iconColor = isDelete ? theme.palette.error.main : theme.palette.warning.main;
  
  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-description"
      maxWidth="sm"
      fullWidth
      PaperProps={{
        elevation: 3,
        sx: {
          // Add conditional styling for error/warning state
          ...(isDelete && {
            border: `1px solid ${alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.2 : 0.1)}`,
            boxShadow: theme.palette.mode === 'dark'
              ? `0 8px 20px rgba(0,0,0,0.4), 0 0 0 1px ${alpha(theme.palette.error.main, 0.1)}`
              : `0 8px 20px rgba(0,0,0,0.15), 0 0 0 1px ${alpha(theme.palette.error.main, 0.05)}`,
          }),
          // Add subtle outline for non-error state
          ...(!isDelete && {
            border: `1px solid ${alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.15 : 0.1)}`,
            boxShadow: theme.palette.mode === 'dark'
              ? `0 8px 20px rgba(0,0,0,0.3), 0 0 0 1px ${alpha(theme.palette.background.light, 0.1)}`
              : `0 8px 20px rgba(0,0,0,0.1)`,
          }),
          overflow: 'hidden',
          backdropFilter: 'blur(8px)',
        },
      }}
      // Apply backdrop styling
      BackdropProps={{
        sx: {
          backgroundColor: isDelete
            ? alpha(theme.palette.error.dark, theme.palette.mode === 'dark' ? 0.2 : 0.05)
            : undefined,
          backdropFilter: 'blur(4px)',
        },
      }}
      // Add animation transitions
      TransitionProps={{
        style: {
          transitionDuration: '250ms',
        },
      }}
    >
      <DialogTitle id="confirm-dialog-title" sx={{ pr: 6 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconComponent
            sx={{
              color: iconColor,
              mr: 1.5,
              fontSize: 28,
              // Add subtle glow effect for better visibility in dark mode
              ...(theme.palette.mode === 'dark' && {
                filter: `drop-shadow(0 0 3px ${alpha(iconColor, 0.4)})`,
              }),
            }}
          />
          <Typography
            variant="h6"
            component="span"
            sx={{
              fontWeight: 600,
              color: theme.palette.mode === 'dark'
                ? theme.palette.common.white
                : theme.palette.common.black,
            }}
          >
            {title}
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={handleCancel}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.mode === 'dark'
              ? alpha(theme.palette.common.white, 0.7)
              : theme.palette.grey[500],
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark'
                ? alpha(theme.palette.error.main, 0.15)
                : alpha(theme.palette.error.main, 0.08),
              color: theme.palette.error.main,
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 2, pb: 3 }}>
        {typeof content === 'string' ? (
          <DialogContentText
            id="confirm-dialog-description"
            sx={{
              color: theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.85)
                : theme.palette.text.secondary,
              fontSize: '0.95rem',
              lineHeight: 1.6,
              // Add subtle emphasis for delete confirmations
              ...(isDelete && {
                fontWeight: 500,
                '& strong': {
                  color: theme.palette.error.main,
                  fontWeight: 600,
                },
              }),
            }}
          >
            {content}
          </DialogContentText>
        ) : (
          content
        )}
      </DialogContent>

      <DialogActions sx={{
        px: 3,
        pb: 3,
        // Add subtle separation line
        borderTop: `1px solid ${theme.palette.mode === 'dark'
          ? alpha(theme.palette.background.light, 0.1)
          : alpha(theme.palette.background.light, 0.2)
        }`,
        pt: 2,
      }}>
        <Button
          onClick={handleCancel}
          color="inherit"
          variant="outlined"
          disabled={isLoading}
          sx={{
            borderColor: theme.palette.mode === 'dark'
              ? alpha(theme.palette.common.white, 0.3)
              : alpha(theme.palette.common.black, 0.2),
            color: theme.palette.mode === 'dark'
              ? theme.palette.common.white
              : theme.palette.common.black,
            '&:hover': {
              borderColor: theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.5)
                : alpha(theme.palette.common.black, 0.3),
              backgroundColor: theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.05)
                : alpha(theme.palette.common.black, 0.05),
            },
            // Style for disabled state
            '&.Mui-disabled': {
              color: theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.3)
                : alpha(theme.palette.common.black, 0.3),
              borderColor: theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.1)
                : alpha(theme.palette.common.black, 0.1),
            },
          }}
        >
          {cancelText}
        </Button>
        <Button
          onClick={handleConfirm}
          color={isDelete ? 'error' : confirmButtonColor}
          variant="contained"
          autoFocus
          disabled={isLoading}
          sx={{
            fontWeight: 600,
            boxShadow: theme.palette.mode === 'dark'
              ? `0 2px 6px ${alpha(isDelete ? theme.palette.error.main : theme.palette.secondary.main, 0.3)}`
              : undefined,
            // Style for disabled state
            '&.Mui-disabled': {
              color: theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.3)
                : undefined,
              backgroundColor: theme.palette.mode === 'dark'
                ? alpha(isDelete ? theme.palette.error.main : theme.palette[confirmButtonColor].main, 0.15)
                : undefined,
            },
          }}
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;